import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import { useProfile } from "../../../shared/hooks/useProfile";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { theme } from '@shared/utils/themeColors';

/**
 * Generic success page for subscription changes
 * Used for changes that don't have a specific success page
 */
const SubscriptionChangeSuccessPage: React.FC = () => {
  const { action } = useParams<{ action: string }>();
  const navigate = useNavigate();
  const { user, isLoading, error } = useProfile();
  const [title, setTitle] = useState('Subscription Updated');
  const [message, setMessage] = useState('Your subscription has been successfully updated.');

  useEffect(() => {
    // Set appropriate messages based on the action
    if (action) {
      switch (action) {
        case 'upgrade':
          setTitle('Subscription Upgraded');
          setMessage('Your subscription has been successfully upgraded. You now have access to additional features and benefits.');
          break;
        case 'update-payment':
          setTitle('Payment Method Updated');
          setMessage('Your payment method has been successfully updated.');
          break;
        case 'pause':
          setTitle('Subscription Paused');
          setMessage('Your subscription has been paused. You can resume it at any time from your subscription settings.');
          break;
        default:
          // Use default messages
          break;
      }
    }
  }, [action]);

  const handleBackToSettings = () => {
    navigate('/settings/plans');
  };

  // Content for the success message
  const renderSuccessContent = () => {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className={`text-2xl font-bold text-center ${theme.text.primary} mb-4`}>
            {title}
          </h2>
          
          <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
            {message}
          </p>
          
          <div className="flex justify-center">
            <button
              onClick={handleBackToSettings}
              className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300`}
            >
              Back to Subscription Settings
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1 className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}>
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              {isLoading ? (
                <div className="flex items-center justify-center w-full p-8">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <div className={`p-8 text-center ${theme.text.error}`}>
                  Error loading user data. Please try again.
                </div>
              ) : (
                renderSuccessContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionChangeSuccessPage;
